/**
 * LocationSearch — Rightmove-style location autocomplete for CITIES + DISTRICTS.
 *
 * Debounced lookups against `/locations/search` (EN + AR). The user must PICK a
 * suggestion; each resolves to an exact `cityId` (kind='city') or `areaId`
 * (kind='area') so downstream search filtering is 100% deterministic. Cities are
 * listed first, then districts (each labelled "District, City"). Every item
 * carries its centroid (latitude/longitude) for the optional radius search.
 *
 * Generalises the older district-only `DistrictSearch`.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { scoreTint, type LocationSuggestion } from '../../services/neighborhoodMapService';
import { useLocationSuggestions } from '../../hooks/useLocationSuggestions';

interface LocationSearchProps {
  onSelect: (location: LocationSuggestion) => void;
  /** Scope district suggestions to a city (optional). */
  cityId?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export default function LocationSearch({
  onSelect,
  cityId,
  placeholder,
  className = '',
  autoFocus = false,
}: LocationSearchProps) {
  const { t, i18n } = useTranslation('search');
  const isRTL = i18n.language === 'ar';

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const localizedName = useCallback(
    (l: LocationSuggestion) => (isRTL ? l.nameAr || l.nameEn : l.nameEn),
    [isRTL],
  );
  const localizedCity = useCallback(
    (l: LocationSuggestion) => (isRTL ? l.cityNameAr || l.cityNameEn : l.cityNameEn) || '',
    [isRTL],
  );

  // Shared debounced lookup (also powers the home-hero unified input).
  const { locations: results, loadingLocations: loading } = useLocationSuggestions(query, cityId);

  // Open the list + reset the cursor whenever a fresh result set lands.
  useEffect(() => {
    if (results.length > 0) {
      setOpen(true);
      setActiveIndex(-1);
    }
  }, [results]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const choose = (location: LocationSuggestion) => {
    onSelect(location);
    setQuery(''); // clearing the term empties the hook's results
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      choose(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <i className={`ri-search-line absolute top-1/2 -translate-y-1/2 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`}></i>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          placeholder={placeholder ?? t('search.location.placeholder', { defaultValue: isRTL ? 'مدينة أو حي…' : 'City or district…' })}
          className={`w-full py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#004D40] ${isRTL ? 'pr-9 pl-3' : 'pl-9 pr-3'}`}
          aria-label={t('search.location.label', { defaultValue: isRTL ? 'ابحث عن مدينة أو حي' : 'Search city or district' })}
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {loading && (
          <i className={`ri-loader-4-line animate-spin absolute top-1/2 -translate-y-1/2 text-[#004D40] ${isRTL ? 'left-3' : 'right-3'}`}></i>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
          {results.length === 0 ? (
            !loading && (
              <div className="px-4 py-3 text-sm text-gray-500">
                {t('search.location.noResults', { defaultValue: isRTL ? 'لا توجد مواقع مطابقة' : 'No matching locations' })}
              </div>
            )
          ) : (
            results.map((loc, idx) => {
              const isCity = loc.kind === 'city';
              const subtitle = isCity
                ? t('search.location.cityLabel', { defaultValue: isRTL ? 'مدينة' : 'City' })
                : (localizedCity(loc)
                  || t('search.location.districtLabel', { defaultValue: isRTL ? 'حي' : 'District' }));
              return (
                <button
                  key={`${loc.kind}-${loc.id}`}
                  type="button"
                  onMouseEnter={() => setActiveIndex(idx)}
                  onClick={() => choose(loc)}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-start ${
                    idx === activeIndex ? 'bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <i className={`${isCity ? 'ri-building-2-line' : 'ri-map-pin-2-line'} text-[#004D40] flex-shrink-0`}></i>
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-gray-900 dark:text-white">{localizedName(loc)}</span>
                      <span className="block truncate text-[11px] text-gray-400">{subtitle}</span>
                    </span>
                  </span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    {!isCity && loc.score != null && (
                      <span
                        className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold text-white"
                        style={{ backgroundColor: scoreTint(loc.score).fill }}
                        title={t('search.location.districtScore', { defaultValue: isRTL ? 'درجة الحي' : 'District score' })}
                      >
                        <i className="ri-star-fill text-[9px]" aria-hidden></i>
                        {loc.score.toFixed(1)}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {t('search.location.listings', { count: loc.activeListingCount, defaultValue: `${loc.activeListingCount} listings` })}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
